import mergeRefs from 'merge-refs';
import {
  type ComponentRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';

import { useInViewport } from '@/hooks/useInViewport';
import { useSessionStorage } from '@/hooks/useSessionStorage';
import { afterFrames } from '@/lib/animate';

const SESSION_STORAGE_KEY = 'auto-scroll-paused';
const SCROLL_DEBOUNCE_MS = 150;
const FOLLOW_EASE = 0.22;
const FOLLOW_SNAP_PX = 1.5;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Props for the AutoScrollTrigger component
 */
export interface AutoScrollTriggerProps {
  /**
   * Unique identifier for this auto-scroll instance
   */
  id: string;
  /**
   * Callback invoked when the trigger element is out of viewport,
   * indicating that auto-scroll should occur
   */
  onShouldScroll(): void;
  /**
   * Auto-scroll sensitivity in pixels. Larger value = more sensitive (scrolls more eagerly).
   * @default 25
   */
  sensitivity?: number;
  onPausedChange?: (paused: boolean) => void;
  smoothFollow?: boolean;
}

/**
 * Imperative handle exposed by AutoScrollTrigger via ref
 */
export interface AutoScrollTriggerHandle {
  /**
   * Programmatically trigger an auto-scroll to this element
   * @param options - Standard ScrollIntoViewOptions
   */
  autoScroll: (options?: ScrollIntoViewOptions) => void;
  resume: (options?: ScrollIntoViewOptions) => void;
}

/**
 * AutoScrollTrigger - A viewport-aware scroll trigger component
 *
 * This component should be placed at the end of a scrollable list (e.g., chat messages,
 * transcripts, activity feeds). It monitors its visibility and triggers auto-scroll
 * behavior when new content is added.
 *
 * @remarks
 * **How it works:**
 * - Places an invisible trigger element at the bottom of your content
 * - Uses Intersection Observer to detect when trigger is out of viewport
 * - Calls `onShouldScroll` when trigger becomes invisible (indicating new content pushed it out)
 * - Exposes `autoScroll()` method via ref for manual scroll control
 *
 * **Sensitivity:**
 * - Higher `sensitivity` value = more eager auto-scrolling
 * - Creates spacing before the trigger, affecting when it exits viewport
 * - Default 25px works well for most use cases
 *
 * **Pause Behavior:**
 * - Auto-scroll pauses when the user scrolls up
 * - Pause state persists in sessionStorage by id (per-tab, cleared on close)
 * - Debounces scroll detection (150ms) - blocks `onShouldScroll` during active scrolling
 * - Resumes when trigger re-enters viewport (user scrolls to bottom)
 *
 * @example
 * ```tsx
 * const scrollTriggerRef = useRef<AutoScrollTriggerHandle>(null);
 *
 * const handleShouldScroll = () => {
 *   scrollTriggerRef.current?.autoScroll();
 * };
 *
 * return (
 *   <div className="overflow-y-auto">
 *     {messages.map(msg => <Message key={msg.id} {...msg} />)}
 *     <AutoScrollTrigger
 *       id="chat-messages"
 *       ref={scrollTriggerRef}
 *       onShouldScroll={handleShouldScroll}
 *       sensitivity={25}
 *     />
 *   </div>
 * );
 * ```
 */
/**
 * Finds the nearest scrollable ancestor element
 */
function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;

  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent);
    if (
      overflow === 'auto' ||
      overflow === 'scroll' ||
      overflowY === 'auto' ||
      overflowY === 'scroll'
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

export const AutoScrollTrigger = forwardRef<
  AutoScrollTriggerHandle,
  AutoScrollTriggerProps
>(function AutoScrollTrigger(
  {
    id,
    onShouldScroll,
    sensitivity = 25,
    onPausedChange,
    smoothFollow = false,
  },
  forwardedRef
) {
  const { ref: inViewportRef, inViewport } = useInViewport();
  const divRef = useRef<ComponentRef<'div'>>(null);
  const ref = useMemo(() => mergeRefs(inViewportRef, divRef), [inViewportRef]);

  const [pausedMap, setPausedMap] = useSessionStorage<Record<string, boolean>>(
    SESSION_STORAGE_KEY,
    {}
  );
  const isPaused = pausedMap[id] ?? false;
  const isPausedRef = useRef(isPaused);
  isPausedRef.current = isPaused;
  const isScrollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const followRafRef = useRef<number | null>(null);

  const stopFollow = () => {
    if (followRafRef.current !== null) {
      cancelAnimationFrame(followRafRef.current);
      followRafRef.current = null;
    }
  };

  const followToBottom = () => {
    const scrollable = findScrollableParent(divRef.current);
    if (!scrollable || followRafRef.current !== null) return;

    if (prefersReducedMotion()) {
      scrollable.scrollTop = scrollable.scrollHeight;
      return;
    }

    const step = () => {
      followRafRef.current = null;
      if (isPausedRef.current) return;

      const target = scrollable.scrollHeight - scrollable.clientHeight;
      const distance = target - scrollable.scrollTop;
      if (distance <= FOLLOW_SNAP_PX) {
        scrollable.scrollTop = target;
        return;
      }
      scrollable.scrollTop += distance * FOLLOW_EASE;
      followRafRef.current = requestAnimationFrame(step);
    };
    followRafRef.current = requestAnimationFrame(step);
  };

  const autoScroll = (
    { block, behavior }: ScrollIntoViewOptions = {
      block: 'end',
      behavior: 'smooth',
    }
  ) => {
    if (pausedMap[id]) return;

    if (smoothFollow) {
      followToBottom();
      return;
    }

    afterFrames(2, () => {
      if (divRef.current) divRef.current.scrollIntoView({ block, behavior });
    });
  };

  const resume = (
    { block, behavior }: ScrollIntoViewOptions = {
      block: 'end',
      behavior: 'smooth',
    }
  ) => {
    setPausedMap((prev) => (prev[id] ? { ...prev, [id]: false } : prev));

    if (smoothFollow) {
      isPausedRef.current = false;
      followToBottom();
      return;
    }

    afterFrames(2, () => {
      if (divRef.current) divRef.current.scrollIntoView({ block, behavior });
    });
  };

  useImperativeHandle(forwardedRef, () => ({
    autoScroll,
    resume,
  }));

  useEffect(() => stopFollow, []);

  useEffect(() => {
    onPausedChange?.(isPaused);
  }, [isPaused, onPausedChange]);

  useEffect(() => {
    if (inViewport && isPaused)
      setPausedMap((prev) => (prev[id] ? { ...prev, [id]: false } : prev));

    if (!inViewport && !isPaused && !isScrollingTimerRef.current)
      onShouldScroll();
  }, [inViewport, isPaused, onShouldScroll, id, setPausedMap]);

  useEffect(() => {
    const scrollableParent = findScrollableParent(divRef.current);

    if (!scrollableParent) return;

    let prevScrollTop = scrollableParent.scrollTop;

    const handleScroll = () => {
      const scrollTop = scrollableParent.scrollTop;
      const scrolledUp = scrollTop < prevScrollTop - 0.5;
      prevScrollTop = scrollTop;
      if (!scrolledUp) return;

      if (isScrollingTimerRef.current) {
        clearTimeout(isScrollingTimerRef.current);
        isScrollingTimerRef.current = null;
      }

      isPausedRef.current = true;
      stopFollow();
      setPausedMap((prev) => (prev[id] ? prev : { ...prev, [id]: true }));

      isScrollingTimerRef.current = setTimeout(
        () => (isScrollingTimerRef.current = null),
        SCROLL_DEBOUNCE_MS
      );
    };

    scrollableParent.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      scrollableParent.removeEventListener('scroll', handleScroll);

      if (isScrollingTimerRef.current)
        clearTimeout(isScrollingTimerRef.current);
    };
  }, [id, setPausedMap]);

  return (
    <div style={{ marginTop: sensitivity }}>
      <div ref={ref} className="size-1" />
    </div>
  );
});
